import React, { useState } from "react";
import {
    Box,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";

import { ds } from "../../styles/designSystem";
import { AnonAvatar, AuthorLine, StatusChip, StatusSelect } from "./FeedbackBits";
import ActionButton from "../../component/common/ActionButton";
import { feedbackApi } from "./api/feedbackApi";
import { notify } from "../../util/toast";

const C = ds.colors;

/**
 * 피드백 카드 (목록 행). 클릭 시 상세 모달을 연다.
 * @param isAdmin 관리자면 상태 셀렉트 노출.
 */
export default function FeedbackCard({ item, isAdmin, onOpenDetail, onEdit, onDelete, onStatusChange, onToggleLike, onRefresh }) {
    const [menuEl, setMenuEl] = useState(null);
    const closeMenu = () => setMenuEl(null);
    const [showComments, setShowComments] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [posting, setPosting] = useState(false);

    const submitInlineComment = async () => {
        if (!commentText.trim() || posting) return;
        setPosting(true);
        try {
            await feedbackApi.createComment(item.id, { content: commentText.trim() });
            setCommentText("");
            onRefresh?.();
        } catch (e) {
            notify(e?.message || "댓글 등록에 실패했습니다.", "error");
        } finally {
            setPosting(false);
        }
    };

    return (
        <Box
            onClick={() => onOpenDetail?.(item)}
            sx={{
                px: 2.5,
                py: 1.75,
                borderBottom: `1px solid ${C.borderLight}`,
                cursor: "pointer",
                transition: "background-color 120ms",
                "&:hover": { bgcolor: C.rowHover },
            }}
        >
            <Stack direction="row" spacing={1.25}>
                <AnonAvatar author={item.author} />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <AuthorLine
                                author={item.author}
                                mine={item.mine}
                                createdAt={item.createdAt}
                                updatedAt={item.updatedAt}
                            />
                        </Box>

                        {/* 우측 액션 — 상태칩/셀렉트 + 본인 글 메뉴 */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ flexShrink: 0 }}
                        >
                            {isAdmin ? (
                                <StatusSelect
                                    value={item.status}
                                    onChange={(next) => onStatusChange?.(item, next)}
                                />
                            ) : (
                                <StatusChip status={item.status} />
                            )}

                            {(item.mine) && (
                                <>
                                    <IconButton size="small" onClick={(e) => setMenuEl(e.currentTarget)}>
                                        <MoreHorizRoundedIcon sx={{ fontSize: 20, color: C.textMuted }} />
                                    </IconButton>
                                    <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={closeMenu}>
                                        {item.mine && (
                                            <>
                                                <MenuItem
                                                    onClick={() => { closeMenu(); onEdit?.(item); }}
                                                    sx={{ fontSize: 13 }}
                                                >
                                                    수정
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => { closeMenu(); onDelete?.(item); }}
                                                    sx={{ fontSize: 13, color: C.error }}
                                                >
                                                    삭제
                                                </MenuItem>
                                            </>
                                        )}

                                    </Menu>
                                </>
                            )}
                        </Stack>
                    </Stack>

                    {item.title && (
                        <Typography sx={{ mt: 0.75, fontSize: 14.5, fontWeight: 700, color: C.textPrimary }}>
                            {item.title}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.5,
                            fontSize: 14,
                            lineHeight: 1.7,
                            color: "#334155",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            display: "-webkit-box",
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {item.content}
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1, color: C.textMuted }}>
                        <Box component="span"
                            onClick={(e) => { e.stopPropagation(); onToggleLike?.(item); }}
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.3, cursor: "pointer", mr: 1,
                                color: item.likedByMe ? C.error : C.textMuted, "&:hover": { color: C.error } }}
                        >
                            {item.likedByMe
                                ? <FavoriteRoundedIcon sx={{ fontSize: 15 }} />
                                : <FavoriteBorderRoundedIcon sx={{ fontSize: 15 }} />}
                            <Typography sx={{ fontSize: 12.5, color: "inherit" }}>{item.likeCount ?? 0}</Typography>
                        </Box>
                        <Box component="span" onClick={(e) => { e.stopPropagation(); setShowComments((v) => !v); }}
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, cursor: "pointer",
                                color: showComments ? C.primary : C.textMuted, "&:hover": { color: C.primary } }}>
                            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 15 }} />
                            <Typography sx={{ fontSize: 12.5, color: "inherit" }}>
                                댓글 {item.commentCount ?? (item.comments?.length ?? 0)}
                            </Typography>
                        </Box>
                        {(item.attachments?.length ?? 0) > 0 && (
                            <>
                                <AttachFileRoundedIcon sx={{ fontSize: 15, ml: 1 }} />
                                <Typography sx={{ fontSize: 12.5 }}>{item.attachments.length}</Typography>
                            </>
                        )}
                    </Stack>

                    {/* 인라인 댓글 — 카드 아래 펼침(모달 대신). */}
                    {showComments && (
                        <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1.25, pt: 1, borderTop: `1px solid ${C.borderLight}` }}>
                            {(item.comments || []).map((c) => (
                                <Stack key={c.id} direction="row" spacing={1} sx={{ py: 0.6 }}>
                                    <AnonAvatar author={c.author} size={26} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <AuthorLine author={c.author} mine={c.mine} createdAt={c.createdAt} updatedAt={c.updatedAt} mineLabel="내 댓글" />
                                        <Typography sx={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", wordBreak: "break-word", mt: 0.25 }}>
                                            {c.content}
                                        </Typography>
                                    </Box>
                                </Stack>
                            ))}
                            <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mt: 0.5 }}>
                                <TextField fullWidth size="small" multiline maxRows={4} placeholder="댓글 (익명)"
                                    value={commentText} onChange={(e) => setCommentText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitInlineComment(); }} />
                                <ActionButton variant="primary" label="등록" onClick={submitInlineComment}
                                    loading={posting} disabled={!commentText.trim()} sx={{ flexShrink: 0 }} />
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}
